import {useCallback, useEffect, useMemo, useState} from "react";
import type {FormEvent} from "react";
import {
  cancelExecutionPlan,
  createExecutionPlan,
  decideExecutionPlan,
  diffExecutionPlanVersions,
  listExecutionPlanApprovals,
  listExecutionPlanAudit,
  listExecutionPlans,
  listExecutionPlanVersions,
  listWorkPackages,
  submitExecutionPlan,
  updateExecutionPlan,
  validateExecutionPlan,
  type ExecutionPlan,
  type ExecutionPlanApproval,
  type ExecutionPlanAudit,
  type ExecutionPlanDiff,
  type ExecutionPlanDraft,
  type ExecutionPlanValidation,
  type ExecutionPlanVersion,
  type WorkPackage
} from "../api/client";
import {listAutonomousWork, type AutonomousWorkRequest} from "../api/autonomousFactory";
import {useAuth} from "../auth/AuthContext";
import {flowTraceIdFromMap} from "../utils/flowTrace";
import {PageHeader} from "../components/PageHeader";

type Mode = "list" | "create" | "edit" | "preview" | "history" | "approvals" | "audit";
type TaskDraft = ExecutionPlanDraft["tasks"][number];

const engineeringRoles = new Set([
  "workbench-operator", "platform-engineer", "asf-engineer", "ux-engineer",
  "qa-engineer", "security-engineer", "documentation-engineer"
]);

const blankTask = (ownerOrganization: string, ordinal: number): TaskDraft => ({
  taskKey: `TASK-${ordinal}`,
  title: "",
  objective: "",
  ownerOrganization,
  assignedWorker: null,
  requiredWorkerCapability: null,
  dependencies: [],
  inputs: [],
  expectedOutputs: [],
  evidenceRequirements: [],
  risk: "MEDIUM",
  priority: "MEDIUM",
  maximumAttempts: 1,
  backoffSeconds: 0,
  timeoutSeconds: null,
  completionCriteria: []
});

function blankPlan(ownerOrganization: string, workPackageId = ""): ExecutionPlanDraft {
  return {
    planKey: "",
    workPackageId,
    title: "",
    summary: "",
    proposedApproach: "",
    architectureDesignApproach: null,
    scope: [],
    outOfScope: [],
    assumptions: [],
    dependencies: [],
    rollbackRecoveryApproach: null,
    tasks: [blankTask(ownerOrganization, 1)],
    workerAssignments: [],
    milestones: [],
    risks: [],
    expectedDeliverables: [],
    evidenceRequirements: [],
    verificationStrategy: "",
    estimatedEffort: null,
    targetStart: null,
    targetEnd: null,
    namedApprover: "",
    ownerOrganization
  };
}

export function ExecutionPlanning() {
  const {authorizedFetch, user} = useAuth();
  const [plans, setPlans] = useState<ExecutionPlan[]>([]);
  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [flowWork, setFlowWork] = useState<AutonomousWorkRequest[]>([]);
  const [flowUnavailable, setFlowUnavailable] = useState(false);
  const [selected, setSelected] = useState<ExecutionPlan | null>(null);
  const [draft, setDraft] = useState(() => blankPlan(user?.company ?? ""));
  const [mode, setMode] = useState<Mode>("list");
  const [validation, setValidation] = useState<ExecutionPlanValidation | null>(null);
  const [versions, setVersions] = useState<ExecutionPlanVersion[]>([]);
  const [diff, setDiff] = useState<ExecutionPlanDiff | null>(null);
  const [approvals, setApprovals] = useState<ExecutionPlanApproval[]>([]);
  const [audit, setAudit] = useState<ExecutionPlanAudit[]>([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mayAuthor = user?.roles.some(role => engineeringRoles.has(role)) ?? false;

  const refresh = useCallback(async (signal?: AbortSignal) => {
    const [planResult, packageResult, flowWorkResult] = await Promise.all([
      listExecutionPlans(authorizedFetch, undefined, signal),
      listWorkPackages(authorizedFetch, signal),
      listAutonomousWork(authorizedFetch, signal).catch(() => null)
    ]);
    setPlans(planResult);
    setWorkPackages(packageResult);
    setFlowWork(flowWorkResult ?? []);
    setFlowUnavailable(flowWorkResult === null);
  }, [authorizedFetch]);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal).catch(problem => {
      if (!controller.signal.aborted) setError(message(problem));
    });
    return () => controller.abort();
  }, [refresh]);

  const activeWorkPackages = useMemo(
    () => workPackages.filter(workPackage => workPackage.status === "ACTIVE"),
    [workPackages]
  );

  const beginCreate = () => {
    const requested = new URLSearchParams(window.location.search).get("workPackageId") ?? "";
    const workPackage = activeWorkPackages.find(item => item.id === requested) ?? activeWorkPackages[0];
    setDraft(blankPlan(workPackage?.ownerOrganization ?? user?.company ?? "", workPackage?.id));
    setSelected(null);
    setValidation(null);
    setError("");
    setMode("create");
  };

  const beginEdit = (plan: ExecutionPlan) => {
    setSelected(plan);
    setDraft(toDraft(plan));
    setReason("");
    setValidation(null);
    setMode("edit");
  };

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try { await operation(); } catch (problem) { setError(message(problem)); } finally { setBusy(false); }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    await run(async () => {
      const result = mode === "create"
        ? await createExecutionPlan(authorizedFetch, draft)
        : await updateExecutionPlan(authorizedFetch, required(selected), draft, reason || "Governed Draft revision");
      await refresh();
      setSelected(result);
      setDraft(toDraft(result));
      setMode("preview");
    });
  };

  const command = async (action: "validate" | "submit" | "cancel" | "approve" | "reject" | "return-for-revision") => {
    const plan = required(selected);
    if (["cancel", "reject", "return-for-revision"].includes(action) && !reason.trim()) {
      setError("A governed rationale is required.");
      return;
    }
    await run(async () => {
      if (action === "validate") {
        const result = await validateExecutionPlan(authorizedFetch, plan);
        setValidation(result);
        if (result.valid) setSelected({...plan, version: result.version, lifecycle: "VALIDATED", validatedVersion: result.version});
      } else {
        const result = action === "submit"
          ? await submitExecutionPlan(authorizedFetch, plan)
          : action === "cancel"
            ? await cancelExecutionPlan(authorizedFetch, plan, reason)
            : await decideExecutionPlan(authorizedFetch, plan, action, reason || "Exact submitted plan accepted");
        setSelected(result);
        setPlans(current => current.map(item => item.id === result.id ? result : item));
        setValidation(null);
      }
    });
  };

  const showHistory = async (plan: ExecutionPlan) => run(async () => {
    const result = await listExecutionPlanVersions(authorizedFetch, plan.id);
    setSelected(plan);
    setVersions(result);
    setDiff(result.length > 1
      ? await diffExecutionPlanVersions(authorizedFetch, plan.id, result.at(-1)!.version, result[0].version)
      : null);
    setMode("history");
  });

  const showApprovals = async () => run(async () => {
    setApprovals(await listExecutionPlanApprovals(authorizedFetch));
    setMode("approvals");
  });

  const showAudit = async (plan: ExecutionPlan) => run(async () => {
    setSelected(plan);
    setAudit(await listExecutionPlanAudit(authorizedFetch, plan.id));
    setMode("audit");
  });

  return <>
    <PageHeader eyebrow="Planning / Governed execution authority" title="Execution plans"
      description="Translate ACTIVE work packages into versioned task graphs, governed worker assignments, evidence obligations, and an exact named-human approval gate."
      aside={mode === "list" ? <div className="header-actions">
        <button type="button" onClick={() => void showApprovals()}>Approval tasks</button>
        {mayAuthor && <button className="primary-action" type="button" onClick={beginCreate}>Create execution plan</button>}
      </div> : undefined} />
    <section className="panel page-panel execution-plan-page">
      <div className="boundary-banner" role="note"><strong>Governed execution authority</strong>
        <span>An ACTIVE plan authorizes only its assigned workers, approved tools, evidence obligations, policy boundaries, and named-human gates.</span></div>
      {error && <p className="form-message error" role="alert">{error}</p>}
      {flowUnavailable && <p role="status">Flow trace is unavailable. Execution plans remain readable.</p>}
      {mode === "list" && <PlanTable plans={plans} mayAuthor={mayAuthor}
        onPreview={plan => { setSelected(plan); setValidation(null); setMode("preview"); }}
        onEdit={beginEdit} onHistory={plan => void showHistory(plan)} onAudit={plan => void showAudit(plan)} />}
      {(mode === "create" || mode === "edit") && <PlanForm draft={draft} setDraft={setDraft}
        activeWorkPackages={activeWorkPackages} editing={mode === "edit"} reason={reason} setReason={setReason}
        busy={busy} onSubmit={save} onCancel={() => setMode("list")} />}
      {mode === "preview" && selected && <PlanPreview plan={selected} validation={validation}
        flowTraceId={flowTraceForPlan(flowWork, selected)} username={user?.username ?? ""} mayAuthor={mayAuthor} busy={busy} reason={reason} setReason={setReason}
        onBack={() => setMode("list")} onEdit={() => beginEdit(selected)}
        onValidate={() => void command("validate")} onSubmit={() => void command("submit")}
        onDecision={action => void command(action)} onCancel={() => void command("cancel")}
        onHistory={() => void showHistory(selected)} onAudit={() => void showAudit(selected)} />}
      {mode === "history" && selected && <PlanHistory plan={selected} versions={versions} diff={diff}
        onBack={() => setMode("preview")} />}
      {mode === "approvals" && <ApprovalInbox approvals={approvals} onOpen={approval => {
        const plan = plans.find(item => item.id === approval.executionPlanId);
        if (plan) { setSelected(plan); setMode("preview"); }
      }} onBack={() => setMode("list")} />}
      {mode === "audit" && selected && <PlanAudit plan={selected} events={audit}
        flowTraceId={flowTraceForPlan(flowWork, selected)} onBack={() => setMode("preview")} />}
    </section>
  </>;
}

function PlanTable({plans, mayAuthor, onPreview, onEdit, onHistory, onAudit}: {
  plans: ExecutionPlan[]; mayAuthor: boolean;
  onPreview(plan: ExecutionPlan): void; onEdit(plan: ExecutionPlan): void;
  onHistory(plan: ExecutionPlan): void; onAudit(plan: ExecutionPlan): void;
}) {
  if (!plans.length) return <div className="empty-state"><h2>No execution plans in this scope</h2>
    <p>An authorized engineering organization can plan an ACTIVE work package.</p></div>;
  return <div className="table-scroll" tabIndex={0} aria-label="Scrollable execution-plan table"><table><thead><tr>
    <th>Plan</th><th>Work package</th><th>Owner</th><th>Lifecycle</th><th>Version</th><th><span className="visually-hidden">Actions</span></th>
  </tr></thead><tbody>{plans.map(plan => <tr key={plan.id}><td><strong>{plan.planKey}</strong><span>{plan.title}</span></td>
    <td>{plan.workPackageKey}</td><td>{plan.ownerOrganization}</td><td><Status value={plan.lifecycle} /></td><td>v{plan.version}</td>
    <td className="row-actions"><button type="button" onClick={() => onPreview(plan)}>Preview</button>
      {mayAuthor && ["DRAFT", "REVISION_REQUIRED"].includes(plan.lifecycle) && <button type="button" onClick={() => onEdit(plan)}>Edit</button>}
      <button type="button" onClick={() => onHistory(plan)}>History</button><button type="button" onClick={() => onAudit(plan)}>Audit</button></td>
  </tr>)}</tbody></table></div>;
}

function PlanForm({draft, setDraft, activeWorkPackages, editing, reason, setReason, busy, onSubmit, onCancel}: {
  draft: ExecutionPlanDraft; setDraft(value: ExecutionPlanDraft): void; activeWorkPackages: WorkPackage[];
  editing: boolean; reason: string; setReason(value: string): void; busy: boolean;
  onSubmit(event: FormEvent): void; onCancel(): void;
}) {
  const text = (field: keyof ExecutionPlanDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft({...draft, [field]: event.target.value});
  const list = (field: keyof ExecutionPlanDraft) => (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setDraft({...draft, [field]: lines(event.target.value)});
  const updateTask = (index: number, task: TaskDraft) => setDraft({...draft,
    tasks: draft.tasks.map((item, current) => current === index ? task : item)});
  return <form className="authoring-form" onSubmit={onSubmit}>
    <div className="form-heading"><div><p className="eyebrow">{editing ? "Versioned revision" : "New governed plan"}</p>
      <h2>{editing ? `Edit ${draft.planKey}` : "Create execution plan"}</h2></div><span className="badge amber">Draft only</span></div>
    <div className="form-grid">
      <label>Plan key<input required pattern="[A-Z][A-Z0-9-]{2,39}" value={draft.planKey} onChange={text("planKey")} /></label>
      <label>ACTIVE work package<select required disabled={editing} value={draft.workPackageId} onChange={event => {
        const workPackage = activeWorkPackages.find(item => item.id === event.target.value);
        setDraft({...draft, workPackageId: event.target.value,
          ownerOrganization: workPackage?.ownerOrganization ?? draft.ownerOrganization});
      }}><option value="">Select ACTIVE work package</option>{activeWorkPackages.map(item =>
        <option key={item.id} value={item.id}>{item.key} — {item.title}</option>)}</select></label>
      <label>Title<input required value={draft.title} onChange={text("title")} /></label>
      <label>Owner organization<input required value={draft.ownerOrganization} onChange={text("ownerOrganization")} /></label>
      <label className="span-2">Summary<textarea required rows={3} value={draft.summary} onChange={text("summary")} /></label>
      <label className="span-2">Proposed approach<textarea required rows={4} value={draft.proposedApproach} onChange={text("proposedApproach")} /></label>
      <label className="span-2">Architecture / design approach<textarea rows={3} value={draft.architectureDesignApproach ?? ""} onChange={text("architectureDesignApproach")} /></label>
      <ListField label="Scope" value={draft.scope} onChange={list("scope")} />
      <ListField label="Out of scope" value={draft.outOfScope} onChange={list("outOfScope")} />
      <ListField label="Assumptions" value={draft.assumptions} onChange={list("assumptions")} optional />
      <ListField label="Plan dependencies" value={draft.dependencies} onChange={list("dependencies")} optional />
      <ListField label="Expected deliverables" value={draft.expectedDeliverables} onChange={list("expectedDeliverables")} />
      <label>Named human approver<input required pattern="[a-z0-9][a-z0-9._-]{2,199}" value={draft.namedApprover} onChange={text("namedApprover")} /><small>Must be a different resolvable username, never a role label.</small></label>
      <label className="span-2">Verification strategy<textarea required rows={3} value={draft.verificationStrategy} onChange={text("verificationStrategy")} /></label>
      <label>Target start<input type="date" value={draft.targetStart ?? ""} onChange={text("targetStart")} /></label>
      <label>Target end<input type="date" value={draft.targetEnd ?? ""} onChange={text("targetEnd")} /></label>
      <label>Estimated effort<input value={draft.estimatedEffort ?? ""} onChange={text("estimatedEffort")} /></label>
      <label>Rollback / recovery<input value={draft.rollbackRecoveryApproach ?? ""} onChange={text("rollbackRecoveryApproach")} /></label>
      {editing && <label className="span-2">Change reason<input required value={reason} onChange={event => setReason(event.target.value)} /></label>}
    </div>
    <section className="nested-editor" aria-labelledby="tasks-heading"><header><div><h3 id="tasks-heading">Execution tasks</h3>
      <p>Dependencies form a validated directed acyclic graph. No task runs from this screen.</p></div>
      <button type="button" onClick={() => setDraft({...draft, tasks: [...draft.tasks, blankTask(draft.ownerOrganization, draft.tasks.length + 1)]})}>Add task</button></header>
      {draft.tasks.map((task, index) => <TaskEditor key={index} task={task} index={index} update={updateTask}
        remove={() => setDraft({...draft, tasks: draft.tasks.filter((_, current) => current !== index)})} />)}</section>
    <section className="nested-editor"><header><div><h3>Worker assignments, milestones, risks and evidence</h3>
      <p>One JSON object per line; each is contract-validated before the plan can authorize governed runtime execution.</p></div></header>
      <div className="form-grid">
        <JsonLines label="Worker assignments" value={draft.workerAssignments} onChange={workerAssignments => setDraft({...draft, workerAssignments})} />
        <JsonLines label="Milestones" value={draft.milestones} onChange={milestones => setDraft({...draft, milestones})} />
        <JsonLines label="Risks" value={draft.risks} onChange={risks => setDraft({...draft, risks})} />
        <JsonLines label="Evidence requirements" value={draft.evidenceRequirements} onChange={evidenceRequirements => setDraft({...draft, evidenceRequirements})} />
      </div>
    </section>
    <div className="form-actions"><button type="button" onClick={onCancel}>Cancel</button>
      <button className="primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : editing ? "Save immutable version" : "Create Draft"}</button></div>
  </form>;
}

function TaskEditor({task, index, update, remove}: {task: TaskDraft; index: number; update(index: number, task: TaskDraft): void; remove(): void}) {
  const text = (field: keyof TaskDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    update(index, {...task, [field]: event.target.value});
  const list = (field: keyof TaskDraft) => (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    update(index, {...task, [field]: lines(event.target.value)});
  return <fieldset className="task-editor"><legend>Task {index + 1}</legend><div className="form-grid">
    <label>Task key<input required value={task.taskKey} onChange={text("taskKey")} /></label>
    <label>Title<input required value={task.title} onChange={text("title")} /></label>
    <label className="span-2">Objective<textarea required value={task.objective} onChange={text("objective")} /></label>
    <label>Owner organization<input required value={task.ownerOrganization} onChange={text("ownerOrganization")} /></label>
    <label>Required capability<input value={task.requiredWorkerCapability ?? ""} onChange={text("requiredWorkerCapability")} /></label>
    <ListField label="Dependencies (task keys)" value={task.dependencies} onChange={list("dependencies")} optional />
    <ListField label="Inputs" value={task.inputs} onChange={list("inputs")} optional />
    <ListField label="Expected outputs" value={task.expectedOutputs} onChange={list("expectedOutputs")} />
    <ListField label="Evidence requirements" value={task.evidenceRequirements} onChange={list("evidenceRequirements")} />
    <ListField label="Completion criteria" value={task.completionCriteria} onChange={list("completionCriteria")} />
    <label>Risk<select value={task.risk} onChange={event => update(index, {...task, risk: event.target.value as TaskDraft["risk"]})}><option>LOW</option><option>MEDIUM</option><option>HIGH</option></select></label>
    <label>Priority<select value={task.priority} onChange={event => update(index, {...task, priority: event.target.value as TaskDraft["priority"]})}><option>LOW</option><option>MEDIUM</option><option>HIGH</option><option>CRITICAL</option></select></label>
    <label>Maximum attempts<input type="number" min="1" max="5" value={task.maximumAttempts} onChange={event => update(index, {...task, maximumAttempts: Number(event.target.value)})} /></label>
    <label>Timeout seconds<input type="number" min="1" value={task.timeoutSeconds ?? ""} onChange={event => update(index, {...task, timeoutSeconds: event.target.value ? Number(event.target.value) : null})} /></label>
  </div>{index > 0 && <button className="danger-action" type="button" onClick={remove}>Remove task</button>}</fieldset>;
}

function PlanPreview({plan, validation, flowTraceId, username, mayAuthor, busy, reason, setReason, onBack, onEdit, onValidate, onSubmit, onDecision, onCancel, onHistory, onAudit}: {
  plan: ExecutionPlan; validation: ExecutionPlanValidation | null; flowTraceId: string | null; username: string; mayAuthor: boolean; busy: boolean;
  reason: string; setReason(value: string): void; onBack(): void; onEdit(): void; onValidate(): void; onSubmit(): void;
  onDecision(action: "approve" | "reject" | "return-for-revision"): void; onCancel(): void; onHistory(): void; onAudit(): void;
}) {
  const canDecide = plan.lifecycle === "SUBMITTED" && plan.namedApprover === username && plan.author !== username;
  return <article className="package-preview"><header><div><p className="eyebrow">{plan.workPackageKey} / Exact execution authority</p>
    <h2>{plan.planKey} — {plan.title}</h2></div><Status value={plan.lifecycle} /></header>
    <div className="hash-panel"><span>Exact version</span><strong>v{plan.version}</strong><span>Content SHA-256</span><code>{plan.contentHash}</code>
      {flowTraceId && <><span>Flow trace</span><a href={`/flow-traces/${encodeURIComponent(flowTraceId)}`}><code>{flowTraceId}</code></a></>}</div>
    <dl className="preview-meta"><div><dt>Owner organization</dt><dd>{plan.ownerOrganization}</dd></div><div><dt>Author</dt><dd>{plan.author}</dd></div>
      <div><dt>Named approver</dt><dd>{plan.namedApprover}</dd></div><div><dt>Worker boundary</dt><dd>GOVERNED_RUNTIME_POLICY</dd></div></dl>
    <section className="preview-grid"><PreviewList title="Scope" values={plan.scope} /><PreviewList title="Out of scope" values={plan.outOfScope} />
      <PreviewList title="Deliverables" values={plan.expectedDeliverables} /><PreviewList title="Plan dependencies" values={plan.dependencies} /></section>
    <section className="nested-editor"><h3>Task graph</h3><ol className="task-graph">{plan.tasks.map(task => <li key={task.taskKey}>
      <div><strong>{task.taskKey} — {task.title}</strong><p>{task.objective}</p></div><Status value={("status" in task ? task.status : "PLANNED") as string} />
      <small>Depends on: {task.dependencies.join(", ") || "none"} · evidence: {task.evidenceRequirements.length}</small></li>)}</ol></section>
    {validation && <section className={`validation-summary ${validation.valid ? "valid" : "invalid"}`} role="status"><h3>{validation.valid ? "Validation passed" : "Validation failed"}</h3>
      {[...validation.errors, ...validation.warnings].map(issue => <p key={`${issue.field}-${issue.message}`}><strong>{issue.field}</strong> {issue.message}</p>)}</section>}
    {canDecide && <section className="decision-actions" aria-labelledby="decision-heading"><h3 id="decision-heading">Named-human decision</h3>
      <label>Decision comment / required rejection rationale<textarea value={reason} onChange={event => setReason(event.target.value)} /></label>
      <div className="form-actions"><button className="primary-action" type="button" disabled={busy} onClick={() => onDecision("approve")}>Approve and activate exact v{plan.version}</button>
        <button type="button" disabled={busy} onClick={() => onDecision("return-for-revision")}>Return for revision</button>
        <button className="danger-action" type="button" disabled={busy} onClick={() => onDecision("reject")}>Reject</button></div></section>}
    <div className="form-actions"><button type="button" onClick={onBack}>Back</button><button type="button" onClick={onHistory}>Version history and diff</button><button type="button" onClick={onAudit}>Audit</button>
      {mayAuthor && ["DRAFT", "REVISION_REQUIRED"].includes(plan.lifecycle) && <button type="button" onClick={onEdit}>Edit Draft</button>}
      {mayAuthor && plan.lifecycle === "DRAFT" && <button type="button" disabled={busy} onClick={onValidate}>Validate</button>}
      {mayAuthor && plan.lifecycle === "VALIDATED" && <button className="primary-action" type="button" disabled={busy} onClick={onSubmit}>Submit exact version</button>}
      {mayAuthor && ["DRAFT", "VALIDATED", "ACTIVE", "REJECTED", "REVISION_REQUIRED"].includes(plan.lifecycle) && <button className="danger-link" type="button" onClick={onCancel}>Cancel plan</button>}</div>
  </article>;
}

function PlanHistory({plan, versions, diff, onBack}: {plan: ExecutionPlan; versions: ExecutionPlanVersion[]; diff: ExecutionPlanDiff | null; onBack(): void}) {
  return <section className="version-history"><header><div><p className="eyebrow">Immutable ledger</p><h2>{plan.planKey} version history</h2></div><button type="button" onClick={onBack}>Back to preview</button></header>
    {diff && <div className="diff-summary"><strong>v{diff.fromVersion} → v{diff.toVersion}</strong><span>{diff.changedFields.join(", ") || "Lifecycle-only change"}</span></div>}
    <ol>{versions.map(version => <li key={version.id}><span>v{version.version}</span><div><strong>{version.lifecycleEvent}</strong><small>{version.changeReason} · {version.author} · {new Date(version.recordedAt).toLocaleString()}</small></div><code>{version.contentHash.slice(0, 12)}…</code></li>)}</ol></section>;
}

function ApprovalInbox({approvals, onOpen, onBack}: {approvals: ExecutionPlanApproval[]; onOpen(approval: ExecutionPlanApproval): void; onBack(): void}) {
  return <section><div className="form-heading"><div><p className="eyebrow">Exact-version gate</p><h2>Execution-plan approvals</h2></div><button type="button" onClick={onBack}>Back</button></div>
    {!approvals.length ? <div className="empty-state"><h3>No pending approval task in this scope</h3></div> : <div className="card-grid">{approvals.map(approval => <article className="approval-card" key={approval.approvalId}>
      <h3>{approval.planKey} — {approval.title}</h3><p>{approval.workPackageKey} · v{approval.exactVersion}</p><code>{approval.exactHash}</code>
      <dl className="detail-list"><div><dt>Author</dt><dd>{approval.author}</dd></div><div><dt>Named approver</dt><dd>{approval.namedApprover}</dd></div></dl>
      <button type="button" disabled={!approval.canDecide} onClick={() => onOpen(approval)}>{approval.canDecide ? "Review exact submission" : "Read-only status"}</button>
    </article>)}</div>}</section>;
}

function PlanAudit({plan, events, flowTraceId, onBack}: {plan: ExecutionPlan; events: ExecutionPlanAudit[]; flowTraceId: string | null; onBack(): void}) {
  return <section><div className="form-heading"><div><p className="eyebrow">Append-only evidence</p><h2>{plan.planKey} audit</h2>{flowTraceId && <a href={`/flow-traces/${encodeURIComponent(flowTraceId)}`}><code>Flow {flowTraceId}</code></a>}</div><button type="button" onClick={onBack}>Back</button></div>
    <ol className="audit-list">{events.map(event => <li key={event.id}><time>{new Date(event.occurredAt).toLocaleString()}</time><div><strong>{event.action}</strong>
      <span>Work Package {plan.workPackageKey} · Execution Plan {plan.planKey}</span>
      <span>Exact version {event.exactVersion == null ? "—" : `v${event.exactVersion}`} · SHA-256 {event.exactHash ?? "—"}</span>
      <span>Actor {event.actorUsername} ({event.actorType}) · {event.beforeState ?? "—"} → {event.afterState ?? "—"}</span>
      <span>Decision {auditDecision(event)} · Correlation {event.correlationId}</span></div>
      <b>{event.policyDecision} / {event.result}</b></li>)}</ol></section>;
}

function auditDecision(event: ExecutionPlanAudit): string {
  return typeof event.details.decision === "string" ? event.details.decision : event.result;
}

function JsonLines<T>({label, value, onChange}: {label: string; value: T[]; onChange(value: T[]): void}) {
  const [raw, setRaw] = useState(value.map(item => JSON.stringify(item)).join("\n"));
  return <label>{label}<textarea rows={5} value={raw} onChange={event => {
    setRaw(event.target.value);
    try { onChange(event.target.value.split(/\n+/).map(line => line.trim()).filter(Boolean).map(line => JSON.parse(line) as T)); } catch { /* server and form validation report malformed input */ }
  }} /><small>JSON Lines; one contract object per line.</small></label>;
}

function ListField({label, value, onChange, optional = false}: {label: string; value: string[]; onChange(event: React.ChangeEvent<HTMLTextAreaElement>): void; optional?: boolean}) {
  return <label>{label}<textarea required={!optional} rows={3} value={value.join("\n")} onChange={onChange} /><small>One item per line{optional ? "; optional" : ""}.</small></label>;
}

function PreviewList({title, values}: {title: string; values: string[]}) {
  return <section><h3>{title}</h3>{values.length ? <ul>{values.map(value => <li key={value}>{value}</li>)}</ul> : <p>None declared.</p>}</section>;
}

function Status({value}: {value: string}) {
  const safe = ["ACTIVE", "VALIDATED", "READY", "VERIFIED", "COMPLETED"].includes(value);
  const deny = ["REJECTED", "FAILED", "CANCELLED", "BLOCKED"].includes(value);
  return <span className={`badge ${safe ? "safe" : deny ? "deny" : "amber"}`}>{value.replaceAll("_", " ")}</span>;
}

function toDraft(plan: ExecutionPlan): ExecutionPlanDraft {
  return {
    planKey: plan.planKey, workPackageId: plan.workPackageId, title: plan.title, summary: plan.summary,
    proposedApproach: plan.proposedApproach, architectureDesignApproach: plan.architectureDesignApproach,
    scope: plan.scope, outOfScope: plan.outOfScope, assumptions: plan.assumptions, dependencies: plan.dependencies,
    rollbackRecoveryApproach: plan.rollbackRecoveryApproach,
    tasks: plan.tasks.map(task => ({taskKey: task.taskKey, title: task.title, objective: task.objective,
      ownerOrganization: task.ownerOrganization, assignedWorker: task.assignedWorker,
      requiredWorkerCapability: task.requiredWorkerCapability, dependencies: task.dependencies, inputs: task.inputs,
      expectedOutputs: task.expectedOutputs, evidenceRequirements: task.evidenceRequirements, risk: task.risk,
      priority: task.priority, maximumAttempts: task.maximumAttempts, backoffSeconds: task.backoffSeconds,
      timeoutSeconds: task.timeoutSeconds, completionCriteria: task.completionCriteria})),
    workerAssignments: plan.workerAssignments.map(assignment => ({taskKey: assignment.taskKey,
      organization: assignment.organization, workerRole: assignment.workerRole, namedWorker: assignment.namedWorker,
      requiredCapability: assignment.requiredCapability, requiredSkills: assignment.requiredSkills,
      providerRuntimeConstraints: assignment.providerRuntimeConstraints, assignmentReason: assignment.assignmentReason})),
    milestones: plan.milestones, risks: plan.risks, expectedDeliverables: plan.expectedDeliverables,
    evidenceRequirements: plan.evidenceRequirements, verificationStrategy: plan.verificationStrategy,
    estimatedEffort: plan.estimatedEffort, targetStart: plan.targetStart, targetEnd: plan.targetEnd,
    namedApprover: plan.namedApprover, ownerOrganization: plan.ownerOrganization
  };
}

function flowTraceForPlan(work: AutonomousWorkRequest[], plan: ExecutionPlan): string | null {
  const matching = work.filter(item => item.workPackageKey === plan.workPackageKey
    && item.contextAuthority.executionPlanId === plan.id);
  const identities = new Set(matching.map(item => flowTraceIdFromMap(item.contextAuthority)));
  return identities.size === 1 ? [...identities][0] : null;
}

function lines(value: string): string[] { return value.split(/\n+/).map(item => item.trim()).filter(Boolean); }
function required<T>(value: T | null): T { if (!value) throw new Error("No execution plan selected"); return value; }
function message(problem: unknown): string { return problem instanceof Error ? problem.message : "The governed operation failed."; }
